import { useEffect, useState } from "react";
import { ImageIcon, Loader2, CheckCircle2, XCircle } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { tr, trServer } from "../lib/i18n";

const KeyInput = ({ value, onChange, set, hint, disabled, testid, unreadable }) => (
  <>
    <input className="input" dir="ltr" type="password" autoComplete="off" spellCheck={false} value={value} disabled={disabled}
      placeholder={set && !unreadable ? hint : tr("x.paste_key_here")} onChange={(e) => onChange(e.target.value)} data-testid={testid} />
    {unreadable && <p className="text-sm font-semibold text-red-500 mt-1" data-testid={`${testid}-unreadable`}>{tr("x.saved_key_unreadable")}</p>}
  </>
);

function Usage({ u }) {
  if (!u) return null;
  return (
    <div className="text-sm" data-testid="image-ai-usage">
      {tr("x.images_this_month", { p0: u.used, p1: u.limit || "∞" })}
      {u.reached && <div className="mt-2 p-3 rounded-lg bg-amber-500/10 text-amber-600" data-testid="image-ai-limit-reached">{tr("x.image_limit_reached_msg", { p0: u.limit })}</div>}
    </div>
  );
}

function TestResult({ r }) {
  if (!r) return null;
  return (
    <div className={`mt-3 text-sm p-3 rounded-lg ${r.ok ? "bg-emerald-500/10 text-emerald-600" : "bg-red-500/10 text-red-500"}`} data-testid="image-ai-test-result">
      <div className="flex items-start gap-2">
        {r.ok ? <CheckCircle2 size={16} className="mt-0.5" /> : <XCircle size={16} className="mt-0.5" />}
        <span>{r.ok ? tr("x.image_ai_test_ok", { p0: r.provider === "gemini" ? "Gemini" : "OpenAI", p1: r.model }) : trServer(r.message)}</span>
      </div>
      {r.ok && r.image_url && <img src={r.image_url} alt="" className="mt-3 rounded-lg max-w-[220px]" data-testid="image-ai-test-image" />}
    </div>
  );
}

/** Settings → AI: image provider for social posters (Gemini first, OpenAI optional). Keys are write-only. */
export default function ImageAiSection() {
  const { can } = useAuth();
  const canEdit = can("social_accounts", "edit");
  const [cfg, setCfg] = useState(null);
  const [f, setF] = useState({});
  const [busy, setBusy] = useState(false);
  const [testing, setTesting] = useState(false);
  const [result, setResult] = useState(null);
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const fill = (d) => { setCfg(d); setF({ gemini_api_key: "", openai_image_key: "", gemini_image_model: d.gemini_image_model, image_monthly_limit: d.image_monthly_limit, image_fallback_openai: d.image_fallback_openai, openai_image_model: d.openai_image_model }); };

  useEffect(() => { api.get("/social/config").then(({ data }) => fill(data)).catch((e) => toast.error(formatApiError(e))); }, []);

  const save = async () => {
    setBusy(true);
    try {
      const body = { ...f };
      if (!body.gemini_api_key) delete body.gemini_api_key;
      if (!body.openai_image_key) delete body.openai_image_key;
      const { data } = await api.put("/social/config", body);
      fill(data); toast.success(tr("x.saved"));
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  const clear = async (name) => {
    if (!window.confirm(tr("x.remove_saved_key_confirm"))) return;
    try { const { data } = await api.put("/social/config", { [`clear_${name}`]: true }); fill(data); } catch (e) { toast.error(formatApiError(e)); }
  };
  const test = async () => {
    setTesting(true); setResult(null);
    try { const { data } = await api.post("/social/ai/test"); setResult(data); setCfg((c) => ({ ...c, usage: data.usage })); }
    catch (e) { toast.error(formatApiError(e)); } finally { setTesting(false); }
  };

  if (!cfg) return null;
  return (
    <div className="panel p-5 mt-5" data-testid="image-ai-section">
      <div className="eyebrow flex items-center gap-2"><ImageIcon size={13} /> {tr("x.image_ai_for_social_posts")}</div>
      <p className="text-xs text-[var(--gray-400)] mt-1 max-w-2xl">{tr("x.image_ai_help")}</p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4">
        <label className="block"><span className="eyebrow">{tr("x.gemini_api_key")}</span>
          <div className="mt-2"><KeyInput value={f.gemini_api_key} onChange={(v) => set("gemini_api_key", v)} set={cfg.gemini_api_key_set} hint={cfg.gemini_api_key_hint} unreadable={cfg.gemini_api_key_unreadable} disabled={!canEdit} testid="gemini-api-key" /></div>
        </label>
        <label className="block"><span className="eyebrow">{tr("x.gemini_image_model")}</span>
          <input className="input mt-2" list="gemini-models" dir="ltr" value={f.gemini_image_model || ""} disabled={!canEdit} onChange={(e) => set("gemini_image_model", e.target.value.trim())} data-testid="gemini-image-model" />
          <datalist id="gemini-models">{cfg.gemini_models.map((m) => <option key={m} value={m} />)}</datalist>
          <p className="text-[11px] text-[var(--gray-400)] mt-1">{tr("x.gemini_model_hint")}</p>
        </label>
        <label className="block"><span className="eyebrow">{tr("x.monthly_image_limit")}</span>
          <input className="input mt-2" type="number" min="0" value={f.image_monthly_limit ?? 300} disabled={!canEdit} onChange={(e) => set("image_monthly_limit", e.target.value)} data-testid="image-monthly-limit" />
          <p className="text-[11px] text-[var(--gray-400)] mt-1">{tr("x.0_unlimited")}</p>
        </label>
        <label className="flex items-center gap-2 mt-6 text-sm">
          <input type="checkbox" checked={!!f.image_fallback_openai} disabled={!canEdit} onChange={(e) => set("image_fallback_openai", e.target.checked)} data-testid="image-fallback-openai" />
          {tr("x.use_openai_if_gemini_fails")}
        </label>
        {f.image_fallback_openai && <>
          <label className="block"><span className="eyebrow">{tr("x.openai_image_key")}</span>
            <div className="mt-2"><KeyInput value={f.openai_image_key} onChange={(v) => set("openai_image_key", v)} set={cfg.openai_image_key_set} hint={cfg.openai_image_key_hint} unreadable={cfg.openai_image_key_unreadable} disabled={!canEdit} testid="openai-image-key" /></div>
          </label>
          <label className="block"><span className="eyebrow">{tr("x.openai_image_model")}</span>
            <input className="input mt-2" dir="ltr" value={f.openai_image_model || ""} disabled={!canEdit} onChange={(e) => set("openai_image_model", e.target.value.trim())} data-testid="openai-image-model" />
          </label>
        </>}
      </div>
      <div className="mt-4"><Usage u={cfg.usage} /></div>
      {canEdit && (
        <div className="flex flex-wrap gap-2 mt-4">
          <button type="button" className="btn-primary" disabled={busy} onClick={save} data-testid="image-ai-save">{busy ? tr("x.saving") : tr("x.save_changes")}</button>
          <button type="button" className="btn-secondary" disabled={testing || !cfg.gemini_api_key_set && !cfg.openai_image_key_set} onClick={test} data-testid="image-ai-test">
            {testing ? <Loader2 size={14} className="animate-spin" /> : <ImageIcon size={14} />} {tr("x.test_connection")}
          </button>
          {cfg.gemini_api_key_set && <button type="button" className="btn-secondary" onClick={() => clear("gemini_api_key")} data-testid="gemini-clear-key">{tr("x.remove_gemini_key")}</button>}
          {cfg.openai_image_key_set && <button type="button" className="btn-secondary" onClick={() => clear("openai_image_key")} data-testid="openai-image-clear-key">{tr("x.remove_openai_image_key")}</button>}
        </div>
      )}
      <TestResult r={result} />
    </div>
  );
}
